import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Mail,
  Trash2,
  Search,
  Phone,
  Calendar,
  MessageSquare,
  Eye,
  AlertTriangle,
  Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MessagesPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    verifyAdminAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyAdminAndFetch = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${BACKEND_URL}/api/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchMessages();
    } catch (err) {
      navigate('/admin/login');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/messages`);
      setMessages(response.data);
      setFilteredMessages(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching messages:', err);
      toast.error('Failed to load messages');
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term) {
      setFilteredMessages(messages);
      return;
    }

    const filtered = messages.filter(message =>
      message.name.toLowerCase().includes(term.toLowerCase()) ||
      message.email.toLowerCase().includes(term.toLowerCase()) ||
      message.subject.toLowerCase().includes(term.toLowerCase()) ||
      message.message.toLowerCase().includes(term.toLowerCase()) ||
      message.mobile_number.includes(term)
    );
    setFilteredMessages(filtered);
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    setShowViewModal(true);

    // Mark as read
    if (!message.read) {
      try {
        await axios.put(`${BACKEND_URL}/api/admin/messages/${message.id}/mark-read`);
        await fetchMessages();
      } catch (err) {
        console.error('Error marking message as read:', err);
      }
    }
  };

  const handleDeleteClick = (message) => {
    setDeleteTarget(message);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/messages/${deleteTarget.id}`);
      toast.success('Message deleted successfully');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy hh:mm a');
    } catch {
      return dateString;
    }
  };

  const unreadCount = messages.filter(m => !m.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Contact Messages</h1>
                <p className="text-sm text-gray-600">
                  {filteredMessages.length} messages
                  {unreadCount > 0 && ` • ${unreadCount} unread`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Search */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name, email, subject, or message..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Messages List */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-white">
            <CardTitle className="flex items-center gap-2">
              <Inbox className="h-5 w-5 text-blue-600" />
              All Messages
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {filteredMessages.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-2">No messages found</p>
                <p className="text-sm text-gray-400">Messages from contact form will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${
                      !message.read ? 'bg-blue-50 border-blue-200' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {!message.read && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              New
                            </span>
                          )}
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {message.name}
                          </h3>
                        </div>
                        
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            <span>{message.mobile_number}</span>
                            <span className="text-gray-400">•</span>
                            <Mail className="h-3 w-3" />
                            <span className="truncate">{message.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MessageSquare className="h-3 w-3" />
                            <span className="font-medium">Subject:</span>
                            <span className="truncate">{message.subject}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-500">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(message.created_at)}</span>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                          {message.message}
                        </p>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewMessage(message)}
                          className="border-blue-600 text-blue-600 hover:bg-blue-50"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteClick(message)}
                          className="border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Message Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Message Details
            </DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Name</Label>
                  <p className="text-gray-900">{selectedMessage.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Mobile</Label>
                  <p className="text-gray-900">{selectedMessage.mobile_number}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-700">Email</Label>
                <p className="text-gray-900">{selectedMessage.email}</p>
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-700">Subject</Label>
                <p className="text-gray-900">{selectedMessage.subject}</p>
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-700">Message</Label>
                <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-700">Received At</Label>
                <p className="text-gray-600 text-sm">{formatDate(selectedMessage.created_at)}</p>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`)}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Reply via Email
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(`tel:${selectedMessage.mobile_number}`)}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowViewModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this message?
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
              <p className="text-sm font-semibold text-red-900 mb-1">
                From: {deleteTarget.name}
              </p>
              <p className="text-sm text-red-700">
                Subject: {deleteTarget.subject}
              </p>
              <p className="text-xs text-red-600 mt-2">
                This action cannot be undone.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Message'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Label component (if not already imported)
const Label = ({ className, children, ...props }) => (
  <label className={`block text-sm font-medium text-gray-700 ${className || ''}`} {...props}>
    {children}
  </label>
);

export default MessagesPage;
